import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BranchProvider } from "./context/BranchContext";
import { CartProvider } from "./context/CartContext";
import ProtectedRoute from "./components/ProtectedRoute";

import Landing from "./pages/Landing";
import DemoHome from "./pages/DemoHome";
import DemoCheckout from "./pages/DemoCheckout";
import DemoOrderConfirmation from "./pages/DemoOrderConfirmation";
import RestaurantMenu from "./pages/RestaurantMenu";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import MenuManagement from "./pages/MenuManagement";
import Settings from "./pages/Settings";
import BranchEdit from "./pages/BranchEdit";
import Summary from "./pages/Summary";
import ManualOrder from "./pages/ManualOrder";
import Employees from "./pages/Employees";
import QRPoster from "./pages/QRPoster";
import Admin from "./pages/Admin";
import DemoShell, { DemoPageShell } from "./components/DemoShell";
import AdminNotifications from "./pages/AdminNotifications";

import "./App.css";

const TOAST_OPTIONS = { style: { fontFamily: "Tajawal, sans-serif" } };
const MENU_PERMS = ["products_add", "products_edit", "products_delete"];
const SETTINGS_PERMS = ["hours_edit", "availability_toggle", "qr_manage"];

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-center text-gray-500">جاري التحميل…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "SUPER_ADMIN") return <Navigate to="/admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <div className="App" dir="rtl">
      <AuthProvider>
        <BranchProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors dir="rtl" toastOptions={TOAST_OPTIONS} />
            <Routes>
              <Route path="/" element={<Landing />} />

              {/* Demo experience */}
              <Route path="/demo" element={<DemoHome />} />
              <Route path="/demo/customer" element={<DemoShell initial="customer" />} />
              <Route path="/demo/checkout" element={<DemoPageShell><DemoCheckout /></DemoPageShell>} />
              <Route path="/demo/order/:id" element={<DemoPageShell><DemoOrderConfirmation /></DemoPageShell>} />
              <Route path="/demo/owner" element={<DemoShell initial="owner" />} />

              {/* Real customer routes — /r/:slug is the canonical public URL. /restaurant/:slug kept as legacy alias. */}
              <Route path="/r/:slug" element={<RestaurantMenu />} />
              <Route path="/r/:slug/:branchCode" element={<RestaurantMenu />} />
              <Route path="/r/:slug/cart" element={<Cart />} />
              <Route path="/r/:slug/:branchCode/cart" element={<Cart />} />
              <Route path="/r/:slug/checkout" element={<Checkout />} />
              <Route path="/r/:slug/:branchCode/checkout" element={<Checkout />} />
              <Route path="/restaurant/:slug" element={<RestaurantMenu />} />
              <Route path="/restaurant/:slug/cart" element={<Cart />} />
              <Route path="/restaurant/:slug/checkout" element={<Checkout />} />
              <Route path="/order/:id" element={<OrderConfirmation />} />

              {/* Real auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/app" element={<RoleRedirect />} />

              {/* Protected */}
              <Route path="/dashboard" element={<ProtectedRoute roles={["OWNER","EMPLOYEE"]}><Dashboard /></ProtectedRoute>} />
              <Route path="/dashboard/menu" element={<ProtectedRoute roles={["OWNER","EMPLOYEE"]} perms={MENU_PERMS}><MenuManagement /></ProtectedRoute>} />
              <Route
                path="/dashboard/settings"
                element={
                  <ProtectedRoute roles={["OWNER", "EMPLOYEE"]} perms={SETTINGS_PERMS}>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/settings/branches/:branchId"
                element={<ProtectedRoute roles={["OWNER"]}><BranchEdit /></ProtectedRoute>}
              />
              <Route path="/dashboard/summary" element={<ProtectedRoute roles={["OWNER","EMPLOYEE"]} perms={["reports_view"]}><Summary /></ProtectedRoute>} />
              <Route path="/dashboard/new-order" element={<ProtectedRoute roles={["OWNER","EMPLOYEE"]}><ManualOrder /></ProtectedRoute>} />
              <Route path="/dashboard/employees" element={<ProtectedRoute roles={["OWNER"]}><Employees /></ProtectedRoute>} />
              <Route
                path="/dashboard/qr-poster"
                element={
                  <ProtectedRoute roles={["OWNER", "EMPLOYEE"]} perms={["qr_manage"]}>
                    <QRPoster />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin/qr-poster" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><QRPoster /></ProtectedRoute>} />
              <Route path="/admin/notifications" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminNotifications /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><Admin /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CartProvider>
        </BranchProvider>
      </AuthProvider>
    </div>
  );
}
